// Admin: gebruikersbeheer in eigen tenant. PRD-01 §5.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { AdminLayout } from "@/components/AdminLayout";

interface AdminUser {
  id: string;
  email: string;
  role: "client" | "counselor" | "municipality" | "admin" | "super_admin";
  isActive: boolean;
  isLocked: boolean;
  lockedUntil: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

export function AdminUsersPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: ["users", search],
    queryFn: () =>
      api.get<{ users: AdminUser[] }>(
        `/v1/users${search ? `?search=${encodeURIComponent(search)}` : ""}`
      ),
  });

  // MFA intrekken. Nodig sinds counselors MFA-plichtig zijn: raakt iemand zijn
  // telefoon én zijn backup-codes kwijt, dan is dit de enige weg terug. De
  // motivatie is verplicht en belandt in het auditlog — het haalt tenslotte
  // tijdelijk de tweede factor van een account met dossiertoegang.
  const resetMfa = useMutation({
    mutationFn: ({ id, motivation }: { id: string; motivation: string }) =>
      api.post(`/v1/users/${id}/mfa/reset`, { motivation }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  const unlock = useMutation({
    mutationFn: (id: string) => api.post(`/v1/users/${id}/unlock`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
    onError: (e) => setErrorKey(e instanceof ApiError ? e.i18nKey : "errors.internal"),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
    onError: (e) => setErrorKey(e instanceof ApiError ? e.i18nKey : "errors.internal"),
  });

  return (
    <AdminLayout title={t("admin.users.title", "Gebruikers")}>
      <div className="bg-white rounded-xl border border-neutral-200">
        <header className="px-5 py-4 border-b border-neutral-200 flex items-center gap-3">
          <input
            type="search"
            placeholder={t("admin.users.search", "Zoek op e-mail")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 max-w-xs px-3 py-2 border border-neutral-300 rounded-lg text-sm"
          />
          <span className="text-xs text-neutral-500">
            {usersQuery.data?.users.length ?? 0} {t("admin.users.count", "gebruikers")}
          </span>
        </header>

        {errorKey && (
          <div role="alert" className="m-5 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
            {t(errorKey)}
          </div>
        )}

        {usersQuery.isLoading ? (
          <p className="p-5 text-sm text-neutral-500">{t("loading", "Laden…")}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600">
              <tr>
                <th className="text-left px-5 py-2 font-medium">{t("admin.users.col.email", "E-mail")}</th>
                <th className="text-left px-5 py-2 font-medium">{t("admin.users.col.role", "Rol")}</th>
                <th className="text-left px-5 py-2 font-medium">{t("admin.users.col.status", "Status")}</th>
                <th className="text-left px-5 py-2 font-medium">{t("admin.users.col.lastLogin", "Laatste login")}</th>
                <th className="px-5 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {usersQuery.data?.users.map((u) => (
                <tr key={u.id} className="border-t border-neutral-100">
                  <td className="px-5 py-3 font-medium">{u.email}</td>
                  <td className="px-5 py-3 text-neutral-600">{u.role}</td>
                  <td className="px-5 py-3">
                    {!u.isActive ? (
                      <span className="text-red-700 text-xs">{t("admin.users.deactivated", "Gedeactiveerd")}</span>
                    ) : u.isLocked ? (
                      <span className="text-amber-700 text-xs">{t("admin.users.locked", "Vergrendeld")}</span>
                    ) : (
                      <span className="text-green-700 text-xs">{t("admin.users.active", "Actief")}</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-neutral-600 text-xs">
                    {u.lastLoginAt
                      ? new Date(u.lastLoginAt).toLocaleString("nl-NL", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })
                      : t("admin.users.never", "Nog niet")}
                  </td>
                  <td className="px-5 py-3 text-right space-x-3">
                    {u.isLocked && (
                      <button
                        onClick={() => unlock.mutate(u.id)}
                        className="text-sm text-brand-600 hover:text-brand-800"
                      >
                        {t("admin.users.unlock", "Ontgrendelen")}
                      </button>
                    )}
                    {u.isActive && (
                      <button
                        onClick={() => {
                          const motivation = prompt(
                            t(
                              "admin.users.mfaResetPrompt",
                              "MFA intrekken voor {{email}}. Waarom? Dit komt in het auditlog en de gebruiker wordt uitgelogd.",
                              { email: u.email }
                            )
                          );
                          if (motivation && motivation.trim().length >= 10) {
                            resetMfa.mutate({ id: u.id, motivation: motivation.trim() });
                          } else if (motivation !== null) {
                            alert(
                              t(
                                "admin.users.mfaResetNeedsReason",
                                "Geef een reden van minstens tien tekens."
                              )
                            );
                          }
                        }}
                        className="text-sm text-amber-700 hover:text-amber-900"
                      >
                        {t("admin.users.mfaReset", "MFA intrekken")}
                      </button>
                    )}
                    {u.isActive && (
                      <button
                        onClick={() => {
                          if (confirm(t("admin.users.confirmDeactivate", "Account deactiveren?"))) {
                            deactivate.mutate(u.id);
                          }
                        }}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        {t("admin.users.deactivate", "Deactiveren")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}
